import type { CampaignShopifyConfigInput } from './hooks/useCampaignShopify';

const PREFIX = 'discovr:shopify-coupon-draft:';
// Long enough to survive a same-day interruption (lunch, a meeting, a
// browser restart) without holding a draft so stale it risks mismatching
// product/collection/campaign state that may have changed since.
const TTL_MS = 24 * 60 * 60 * 1000;

export interface StoredDraft {
  form: CampaignShopifyConfigInput;
  savedAt: number;
}

function draftKey(campaignId: string): string {
  return `${PREFIX}${campaignId}`;
}

/** Reads a not-yet-expired local draft of a campaign's unsaved Shopify
 *  settings form, or null if there isn't one, it expired, or storage is
 *  unavailable (private window, blocked site data) — never throws. */
export function readShopifyDraft(campaignId: string): CampaignShopifyConfigInput | null {
  return readShopifyDraftEntry(campaignId)?.form ?? null;
}

/** Like `readShopifyDraft`, plus when the draft was written — the restore
 *  notice shows its age. */
export function readShopifyDraftEntry(campaignId: string): StoredDraft | null {
  try {
    const raw = localStorage.getItem(draftKey(campaignId));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StoredDraft;
    if (!parsed.form || !Number.isFinite(parsed.savedAt) || Date.now() - parsed.savedAt > TTL_MS) {
      localStorage.removeItem(draftKey(campaignId));
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

/** "just now", "5 minutes ago", "3 hours ago" for the restore notice. */
export function describeDraftAge(savedAt: number, now: number = Date.now()): string {
  const minutes = Math.max(0, Math.floor((now - savedAt) / 60_000));
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes} minute${minutes === 1 ? '' : 's'} ago`;
  const hours = Math.floor(minutes / 60);
  return `${hours} hour${hours === 1 ? '' : 's'} ago`;
}

/** Persists the current unsaved form so switching campaign tabs and coming
 *  back within the TTL restores it. Call only while the form actually has
 *  unsaved edits. */
export function writeShopifyDraft(campaignId: string, form: CampaignShopifyConfigInput): void {
  try {
    const entry: StoredDraft = { form, savedAt: Date.now() };
    localStorage.setItem(draftKey(campaignId), JSON.stringify(entry));
  } catch {
    /* ignore — private window, quota exceeded, etc. */
  }
}

/** Clears one campaign's draft — called once its form is actually saved, so
 *  a stale draft never shadows a fresh server fetch. */
export function clearShopifyDraft(campaignId: string): void {
  try {
    localStorage.removeItem(draftKey(campaignId));
  } catch {
    /* ignore */
  }
}

/** Wipes every campaign's Shopify draft — called on sign-out so a draft
 *  never survives a logout/login, even for the same user (it was never
 *  saved, so it shouldn't outlive the session that created it). */
export function clearAllShopifyDrafts(): void {
  try {
    const keys: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key?.startsWith(PREFIX)) keys.push(key);
    }
    keys.forEach((k) => localStorage.removeItem(k));
  } catch {
    /* ignore */
  }
}
